import { Injectable } from '@angular/core';
import { SelectableField } from './../../models/selectable-field';

import { MatSnackBar } from '@angular/material/snack-bar';
import htmldiff from 'htmldiff-js';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { App, KHFileInfo, Project, Site, SiteActivityLog, SiteActivityLogViewModel } from '../../models';
import { SITE_ACTIVITY_LOG_CONSTANTS } from '../../shared/constants';
import { FieldType } from '../../shared/enums';
import { SiteActivityLogDataService } from '../data-services/site-activity-log-data.service';
import { DateConversionService } from '../utility-services';
import { SiteActivityLogLogicServiceModel } from './models/site-activity-log-logic-service-model';

@Injectable({
    providedIn: 'root'
})
export class SiteActivityLogLogicService {
    private _siteActivityLogReady = new Subject<boolean>();

    siteActivityLogReady$ = this._siteActivityLogReady.asObservable();

    model: SiteActivityLogLogicServiceModel = new SiteActivityLogLogicServiceModel();

    constructor(private _siteActivityLogDataService: SiteActivityLogDataService, private _snackBar: MatSnackBar, private _dateConversionService: DateConversionService) { }

    getAllSiteActivityLog(app: App, site: Site, project: Project, selectableFields: SelectableField[]): void {
        const rowIdentifierCache = new Map<string, string>();
        const selectableFieldCache = new Map<string, SelectableField | undefined>();

        this.model.isLoading = true;
        this._siteActivityLogDataService.getAllSiteActivityLog(app, site, project)
            .pipe(
                map((log: SiteActivityLog[]) =>
                    log.map((entry): SiteActivityLog => {
                        const siteActivityViewModel = this.convertToViewModel(entry);
                        this.assignCurrentRowIdentifierValue(siteActivityViewModel, site, selectableFields, rowIdentifierCache, selectableFieldCache);
                        return this.assignSelectableFieldValuesToEntry(siteActivityViewModel, selectableFields, selectableFieldCache);
                    }).filter(e => !!e.selectableField)
                )
            )
            .subscribe({
                next: (response: SiteActivityLogViewModel[]) => {
                    this.model.siteActivityLogs = response;
                    this.model.isLoading = false;
                    this.triggerSiteActivityLogReady(true);
                },
                error: () => {
                    this.model.siteActivityLogs = [];
                    this.model.isLoading = false;
                    this.triggerSiteActivityLogReady(false);
                    this._snackBar.open(`Error getting ${app.aliases.site.singular.toLowerCase()} Activity Log`, '', { duration: 2000 });
                }
            });
    }

    triggerSiteActivityLogReady(value: boolean): void {
        this._siteActivityLogReady.next(value);
    }

    clearModel(): void {
        this.model.siteActivityLogs = [];
    }

    parseFileValue(value: any): KHFileInfo[] {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value.map((file) => new KHFileInfo(file));
        }
        else {
            return [];
        }
    }

    private convertToViewModel(entry: SiteActivityLog): SiteActivityLogViewModel {
        const viewModel = new SiteActivityLogViewModel(entry);
        viewModel.timestamp = this._dateConversionService.castToDate(viewModel.timestamp);
        return viewModel;
    }

    private assignCurrentRowIdentifierValue(siteActivityViewModel: SiteActivityLogViewModel, site: Site, selectableFields: SelectableField[], rowIdentifierCache: Map<string, string>, selectableFieldCache: Map<string, SelectableField | undefined>): void {
        if (!siteActivityViewModel.rowId) {
            return;
        }

        const selectableField = this.getCachedSelectableField(selectableFieldCache, siteActivityViewModel, selectableFields);
        if (!selectableField) {
            return null;
        }
        const rowId = siteActivityViewModel.rowId;
        const gridRowIdentifierId = selectableField?.gridColumnIdentifierId;
        const gridId = selectableField?.identifier.fieldId;
        const rowIdValue = this.getCachedRowIdentifier(rowIdentifierCache, gridId, rowId, gridRowIdentifierId, site);

        if (rowIdValue != siteActivityViewModel.rowIdentifierValue) {
            siteActivityViewModel.originalRowIdentifierValue = siteActivityViewModel.rowIdentifierValue;
        }

        siteActivityViewModel.rowIdentifierValue = rowIdValue;
    }

    private getCachedRowIdentifier(rowIdentifierCache: Map<string, string>, gridId: string, rowId: string, gridRowIdentifierId: string, site: Site): string | null {
        if (!gridId || !rowId || !gridRowIdentifierId || !site?.data) {
            return null;
        }

        const cacheKey = `${gridId}-${rowId}`;
        if (rowIdentifierCache.has(cacheKey)) {
            return rowIdentifierCache.get(cacheKey);
        }

        let identifierValue: string | null = null;
        const grid = site.data[gridId];

        if (grid) {
            const gridRow = grid.find(row => row.id === rowId);
            if (gridRow) {
                identifierValue = gridRow[gridRowIdentifierId];
            }
            else {
                identifierValue = SITE_ACTIVITY_LOG_CONSTANTS.ROW_HAS_BEEN_DELETED;
            }
        }

        rowIdentifierCache.set(cacheKey, identifierValue);
        return identifierValue;
    }

    private getCachedSelectableField(selectableFieldCache: Map<string, SelectableField | undefined>, siteActivityViewModel: SiteActivityLogViewModel, selectableFields: SelectableField[]): SelectableField | undefined {
        const cacheKey = `${siteActivityViewModel.fieldIdentifier.fieldId}-${siteActivityViewModel.fieldIdentifier.gridColumnId}`;
        if (selectableFieldCache.has(cacheKey)) {
            return selectableFieldCache.get(cacheKey);
        }

        const selectableField = selectableFields.find(
            f => f.identifier.fieldId === siteActivityViewModel.fieldIdentifier.fieldId &&
                f.identifier.gridColumnId === siteActivityViewModel.fieldIdentifier.gridColumnId
        );

        selectableFieldCache.set(cacheKey, selectableField);
        return selectableField;
    }

    private assignSelectableFieldValuesToEntry(siteActivityViewModel: SiteActivityLogViewModel, selectableFields: SelectableField[], selectableFieldCache: Map<string, SelectableField | undefined>): SiteActivityLogViewModel {

        const selectableField = this.getCachedSelectableField(selectableFieldCache, siteActivityViewModel, selectableFields);

        if (selectableField?.fieldTypeId === FieldType.TextArea) {
            const textAreaInnerTextChange = this.calculateTextAreaInnerTextChange(siteActivityViewModel.oldFieldValue, siteActivityViewModel.newFieldValue);
            return {
                ...siteActivityViewModel,
                selectableField,
                textAreaInnerTextChange
            };
        }

        if (selectableField?.fieldTypeId === FieldType.Uploader || selectableField?.fieldTypeId === FieldType.PhotoGallery) {
            const { allFiles, addedFiles, deletedFiles } = this.calculateFileChanges(
                siteActivityViewModel.oldFieldValue,
                siteActivityViewModel.newFieldValue
            );

            return {
                ...siteActivityViewModel,
                selectableField,
                allFiles,
                addedFiles,
                deletedFiles
            };
        }

        if (selectableField?.fieldTypeId === FieldType.Person) {
            const { allUsers, addedUsers, deletedUsers } = this.calculateUserChanges(
                siteActivityViewModel.oldFieldValue,
                siteActivityViewModel.newFieldValue
            );

            return {
                ...siteActivityViewModel,
                selectableField,
                allUsers,
                addedUsers,
                deletedUsers
            };
        }

        return {
            ...siteActivityViewModel,
            selectableField,
        };
    }

    private calculateFileChanges(oldValue: any, newValue: any): {
        allFiles: KHFileInfo[];
        addedFiles: KHFileInfo[];
        deletedFiles: KHFileInfo[];
    } {
        const oldValueFiles = this.parseFileValue(oldValue);
        const newValueFiles = this.parseFileValue(newValue);

        const addedFiles = newValueFiles.filter(newFile =>
            !oldValueFiles.some(oldFile => oldFile.uri === newFile.uri)
        );

        const deletedFiles = oldValueFiles.filter(oldFile =>
            !newValueFiles.some(newFile => newFile.uri === oldFile.uri)
        );

        const allFiles = [...oldValueFiles, ...newValueFiles].filter((file, index, self) =>
            index === self.findIndex(f => f.uri === file.uri)
        );

        return { allFiles, addedFiles, deletedFiles };
    }

    private calculateTextAreaInnerTextChange(oldFieldValue: any, newFieldValue: any): string | undefined {
        let textAreaInnerTextChange: string | undefined;

        const parser = new DOMParser();
        const oldValueHTML = parser.parseFromString(oldFieldValue ?? '', 'text/html');
        const newValueHTML = parser.parseFromString(newFieldValue ?? '', 'text/html');
        textAreaInnerTextChange = htmldiff.execute(oldValueHTML.body.innerHTML, newValueHTML.body.innerHTML);

        return textAreaInnerTextChange;
    }

    private calculateUserChanges(oldValue: any, newValue: any): {
        allUsers: string[];
        addedUsers: string[];
        deletedUsers: string[];
    } {

        const oldUsers = Array.isArray(oldValue) ? oldValue : [];
        const newUsers = Array.isArray(newValue) ? newValue : [];

        const addedUsers = newUsers.filter(userId =>
            !oldUsers.includes(userId)
        );

        const deletedUsers = oldUsers.filter(userId =>
            !newUsers.includes(userId)
        );

        const allUsers = [...new Set([...oldUsers, ...newUsers])];

        return { allUsers, addedUsers, deletedUsers };
    }
}
